import { useCallback, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useCreateApplePayLinkMutation } from '../redux/api/authorized/deposit';
import { useSolana } from './useSolana';
import { calculateApplePayFeeAndTotal } from './useApplePay';

const getPaymentUrl = (response) => {
  if (!response) return null;
  if (response.paymentLink?.url) return response.paymentLink.url;
  if (response.data?.paymentLink?.url) return response.data.paymentLink.url;
  return response.url || null;
};

/**
 * Shared helper for launching Apple Pay via the Coinbase onramp hidden webview.
 *
 * Consumers call `startCheckout(amount, callbacks?)` and render the returned `widgetProps`
 * with `<ApplePayWidget {...widgetProps} />`. Callbacks (`onSuccess`, `onError`, etc.) are
 * supplied per invocation to keep flows isolated.
 */
const useApplePayCheckout = () => {
  const [createApplePayLink, { isLoading: isCreatingLink }] =
    useCreateApplePayLinkMutation();
  const { refreshTokenBalances } = useSolana();

  const [paymentUrl, setPaymentUrl] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [error, setError] = useState(null);

  const optionsRef = useRef({});

  const reset = useCallback(() => {
    setPaymentUrl(null);
    setIsProcessingPayment(false);
    setTransactionStatus(null);
    setError(null);
    optionsRef.current = {};
  }, []);

  const handleProcessingChange = useCallback((loading) => {
    setIsProcessingPayment(loading);
    optionsRef.current?.onProcessingChange?.(loading);
  }, []);

  const handleTransactionStatus = useCallback(
    (status) => {
      setTransactionStatus(status);
      if (status === 'pending') {
        optionsRef.current?.onPending?.();
      } else if (status === 'success') {
        if (optionsRef.current?.refreshBalances !== false) {
          refreshTokenBalances();
        }
        optionsRef.current?.onSuccess?.();
        // Once successful we can reset after consumers handle success if requested
        if (optionsRef.current?.resetOnSuccess) {
          reset();
        }
      } else if (status === 'error') {
        optionsRef.current?.onError?.();
      }
      optionsRef.current?.onTransactionStatusChange?.(status);
    },
    [refreshTokenBalances, reset],
  );

  const handleAlert = useCallback((title, message, type) => {
    optionsRef.current?.onAlert?.(title, message, type);
  }, []);

  const startCheckout = useCallback(
    async (amount, callbacks = {}) => {
      optionsRef.current = callbacks || {};

      const normalizedAmount =
        typeof amount === 'number' ? amount : parseFloat(amount ?? '0');
      if (!normalizedAmount || Number.isNaN(normalizedAmount) || normalizedAmount <= 0) {
        const invalidAmountError = new Error('Invalid Apple Pay amount');
        setError(invalidAmountError);
        optionsRef.current?.onError?.(invalidAmountError);
        return Promise.reject(invalidAmountError);
      }

      // Calculate the total amount user should be charged using shared calculation
      // This ensures consistency across the codebase
      const { total: totalUserPays } =
        calculateApplePayFeeAndTotal(normalizedAmount);

      // Send the total amount (base + fee) to Apple Pay so user is charged correctly
      // Round to 2 decimal places to avoid floating point precision issues (e.g., 26.666666666666668 -> 26.67)
      const roundedTotalUserPays = Math.round(totalUserPays * 100) / 100;

      const isWeb = !Capacitor.isNativePlatform();

      try {
        setError(null);
        setTransactionStatus(null);
        const response = await createApplePayLink({
          paymentAmount: roundedTotalUserPays,
          ...(isWeb && { isWeb: true }),
        }).unwrap();

        const url = getPaymentUrl(response);

        if (!url) {
          throw new Error('No Apple Pay payment URL returned');
        }

        setPaymentUrl(url);
        optionsRef.current?.onLinkReady?.(url);
        return url;
      } catch (err) {
        setError(err);
        optionsRef.current?.onError?.(err);
        throw err;
      }
    },
    [createApplePayLink, refreshTokenBalances, reset, handleTransactionStatus],
  );

  const widgetProps = paymentUrl
    ? {
        paymentUrl,
        forceFeature: Boolean(optionsRef.current?.forceFeature),
        setIsProcessingPayment: handleProcessingChange,
        setTransactionStatus: handleTransactionStatus,
        onAlert: handleAlert,
      }
    : null;

  return {
    startCheckout,
    reset,
    widgetProps,
    paymentUrl,
    isStarting: isCreatingLink,
    isProcessingPayment,
    transactionStatus,
    error,
  };
};

export default useApplePayCheckout;
